/* eslint-disable react/display-name */
import Typography from "@bmi-digital/components/typography";
import { transformHyphens } from "@bmi-digital/components/utils";
import classnames from "classnames";
import { graphql } from "gatsby";
import PayloadRichTextNode from "@bmi-digital/payload-rich-text";
import { Table } from "@bmi-digital/payload-rich-text/dist/Table";
import React from "react";
import { GraphQlUploadData } from "@bmi-digital/payload-rich-text/dist/Upload";
import { GraphQlBlockData } from "@bmi-digital/payload-rich-text/dist/Block";
import { classes, StyledRichText } from "./styles/RichTextStyles";
import EmbeddedLink from "./EmbeddedLink";
import InlineLink from "./InlineLink";
import Image from "./image/Image";
import type {
  BlockData,
  LinkData,
  Options,
  RichTextData as PayloadRichTextData
} from "@bmi-digital/payload-rich-text";
import type { ImageRichText } from "./image/types";
import type {
  RichTextAssetLinkBlock,
  RichTextCalculatorLinkBlock,
  RichTextDialogLinkBlock,
  RichTextExternalLinkBlock,
  RichTextHubSpotLinkBlock,
  RichTextInternalLinkBlock,
  RichTextLinkAssetLinkBlock,
  RichTextLinkExternalLinkBlock,
  RichTextLinkHubSpotLinkBlock,
  RichTextLinkInternalLinkBlock,
  RichTextVisualiserLinkBlock
} from "./link/types";
import type { PayloadRichText, PayloadRichTextHeading } from "../types/payload"; // TODO: Match the graphql query

// TODO: Match the graphql query
export type RichTextData = PayloadRichText;

type Settings = {
  theme?: "primary" | "secondary";
  backgroundTheme?: "light" | "dark";
  underlineHeadings?: ("h2" | "h3" | "h4" | "h5" | "h6")[];
  hyperlinkColor?: "default" | "black" | "white";
  gtmLabel?: React.ReactNode;
};

const createInlineLinkLabelFromTextNodes = (
  children: React.ReactElement[]
): string => {
  return children
    .map((node) => {
      if (node.props.data.type === "tab") {
        return " ";
      }
      return node.props.data.text;
    })
    .join("")
    .replace(/\s+/g, " ");
};

const blockIsInternalLinkBlock = (
  block: BlockData | GraphQlBlockData
): block is GraphQlBlockData<RichTextInternalLinkBlock> => {
  return block.fields.blockType === "rich-text-internal-link-block";
};

const blockIsExternalLinkBlock = (
  block: BlockData | GraphQlBlockData
): block is GraphQlBlockData<RichTextExternalLinkBlock> => {
  return block.fields.blockType === "rich-text-external-link-block";
};

const blockIsAssetLinkBlock = (
  block: BlockData | GraphQlBlockData
): block is GraphQlBlockData<RichTextAssetLinkBlock> => {
  return block.fields.blockType === "rich-text-asset-link-block";
};

const blockIsHubSpotLinkBlock = (
  block: BlockData | GraphQlBlockData
): block is GraphQlBlockData<RichTextHubSpotLinkBlock> => {
  return block.fields.blockType === "rich-text-hubspot-cta-link-block";
};

const blockIsDialogLinkBlock = (
  block: BlockData | GraphQlBlockData
): block is GraphQlBlockData<RichTextDialogLinkBlock> => {
  return block.fields.blockType === "rich-text-dialog-link-block";
};

const blockIsCalculatorLinkBlock = (
  block: BlockData | GraphQlBlockData
): block is GraphQlBlockData<RichTextCalculatorLinkBlock> => {
  return block.fields.blockType === "rich-text-calculator-link-block";
};

const blockIsVisualiserLinkBlock = (
  block: BlockData | GraphQlBlockData
): block is GraphQlBlockData<RichTextVisualiserLinkBlock> => {
  return block.fields.blockType === "rich-text-visualiser-link-block";
};

const linkIsInternalLinkBlock = (
  link: LinkData
): link is LinkData<{ hyperlink: [RichTextLinkInternalLinkBlock] }> => {
  return (
    "hyperlink" in link.fields &&
    link.fields.hyperlink &&
    typeof link.fields.hyperlink[0] === "object" &&
    !!link.fields.hyperlink[0] &&
    "blockType" in link.fields.hyperlink[0] &&
    link.fields.hyperlink[0].blockType === "rich-text-link-internal-link-block"
  );
};

const linkIsExternalLinkBlock = (
  link: LinkData
): link is LinkData<{ hyperlink: [RichTextLinkExternalLinkBlock] }> => {
  return (
    "hyperlink" in link.fields &&
    link.fields.hyperlink &&
    typeof link.fields.hyperlink[0] === "object" &&
    !!link.fields.hyperlink[0] &&
    "blockType" in link.fields.hyperlink[0] &&
    link.fields.hyperlink[0].blockType === "rich-text-link-external-link-block"
  );
};

const linkIsAssetLinkBlock = (
  link: LinkData
): link is LinkData<{ hyperlink: [RichTextLinkAssetLinkBlock] }> => {
  return (
    "hyperlink" in link.fields &&
    link.fields.hyperlink &&
    typeof link.fields.hyperlink[0] === "object" &&
    !!link.fields.hyperlink[0] &&
    "blockType" in link.fields.hyperlink[0] &&
    link.fields.hyperlink[0].blockType === "rich-text-link-asset-link-block"
  );
};

const linkIsHubSpotLinkBlock = (
  link: LinkData
): link is LinkData<{ hyperlink: [RichTextLinkHubSpotLinkBlock] }> => {
  return (
    "hyperlink" in link.fields &&
    link.fields.hyperlink &&
    typeof link.fields.hyperlink[0] === "object" &&
    !!link.fields.hyperlink[0] &&
    "blockType" in link.fields.hyperlink[0] &&
    link.fields.hyperlink[0].blockType ===
      "rich-text-link-hubspot-cta-link-block"
  );
};

const uploadIsImage = (
  upload: GraphQlUploadData
): upload is GraphQlUploadData<ImageRichText> => {
  return upload.relationTo === "image";
};

const getOptions = (settings: Settings): Options => {
  return {
    text: (text): React.ReactNode => {
      return transformHyphens(text);
    },
    paragraph: (_paragraphData, children): React.ReactNode => {
      return <Typography gutterBottom>{children}</Typography>;
    },
    heading: (headingData, children): React.ReactNode => {
      const underlineHeadings = settings.underlineHeadings || [];
      return (
        <Typography
          className={classes.title}
          variant={headingData.tag}
          hasUnderline={
            headingData.tag !== "h1" &&
            underlineHeadings.includes(headingData.tag)
          }
        >
          {children}
        </Typography>
      );
    },

    table: (tableData): React.ReactNode => {
      if (!tableData.children || tableData.children.length === 0) {
        return <p>No table data found</p>; // Show message if empty
      }

      return <Table data={tableData} options={getOptions(settings)} />;
    },

    block: (block): React.ReactNode => {
      if (
        blockIsInternalLinkBlock(block) ||
        blockIsExternalLinkBlock(block) ||
        blockIsAssetLinkBlock(block) ||
        blockIsHubSpotLinkBlock(block) ||
        blockIsDialogLinkBlock(block) ||
        blockIsCalculatorLinkBlock(block) ||
        blockIsVisualiserLinkBlock(block)
      ) {
        return <EmbeddedLink fields={block.fields} {...settings} />;
      }
      return null;
    },
    link: (link: LinkData, children: React.ReactElement[]) => {
      if (
        linkIsInternalLinkBlock(link) ||
        linkIsExternalLinkBlock(link) ||
        linkIsAssetLinkBlock(link) ||
        linkIsHubSpotLinkBlock(link)
      ) {
        return (
          <InlineLink
            data={link.fields.hyperlink}
            label={createInlineLinkLabelFromTextNodes(children)}
            hyperlinkColor={settings.hyperlinkColor}
            gtmLabel={settings.gtmLabel}
            data-testid={"rich-text-hyperlink"}
            className={
              link.children[0].type === "text" &&
              link.children[0].textFormat === 1
                ? "bold"
                : ""
            }
          />
        );
      }

      return null;
    },
    upload: (uploadData): React.ReactNode => {
      if (
        "file" in uploadData &&
        typeof uploadData.file === "object" &&
        uploadIsImage(uploadData)
      ) {
        return <Image {...uploadData.file} className="embedded-asset" />;
      }
      return null;
    }
  };
};

const RichText = ({
  document,
  className,
  hasNoBottomMargin,
  "data-testid": testId,
  ...rest
}: {
  document?: RichTextData | null;
  className?: string;
  hasNoBottomMargin?: boolean;
  "data-testid"?: string;
} & Settings) => {
  const data: PayloadRichTextData<"graphql"> | null | undefined = document;
  if (!data) {
    return null;
  }

  return (
    <StyledRichText
      className={classnames(
        classes.root,
        hasNoBottomMargin && classes.noBottomMargin,
        className
      )}
      data-testid={testId}
    >
      <PayloadRichTextNode data={data} options={getOptions(rest)} />
    </StyledRichText>
  );
};

export const parseReachDataRawFields = (
  document: RichTextData
): Record<NonNullable<PayloadRichTextHeading["tag"]>, string | undefined> => {
  return document.root.children.reduce(
    (acc, item) => {
      if (item.type === "heading" && item.tag) {
        return {
          ...acc,
          [item.tag]: item.children
            .filter((child) => child.type === "text")
            .map((child) => child.text)
            .join("")
        };
      }
      return acc;
    },
    {} as Record<NonNullable<PayloadRichTextHeading["tag"]>, string | undefined>
  );
};

export default RichText;

// TODO: Filter down fields to what is needed
export const query = graphql`
  fragment RichTextTextFragment on RichTextText {
    type
    textFormat: format
    text
  }

  fragment RichTextLinkFragmentNonRecursive on RichTextLink {
    type
    children {
      ...RichTextTextFragment
    }
    format
    fields {
      hyperlink {
        ...RichTextLinkInternalLinkBlockFragment
        ...RichTextLinkExternalLinkBlockFragment
        ...RichTextLinkHubSpotLinkBlockFragment
        ...RichTextLinkAssetLinkBlockFragment
      }
    }
  }

  fragment RichTextLinkFragment on RichTextLink {
    ...RichTextLinkFragmentNonRecursive
    fields {
      hyperlink {
        ...RichTextLinkInternalLinkBlockFragment
        ...RichTextLinkExternalLinkBlockFragment
        ...RichTextLinkHubSpotLinkBlockFragment
        ...RichTextLinkAssetLinkBlockFragment
        ...PayloadRichTextDialogLinkBlockFragmentNonRecursive
      }
    }
  }

  fragment RichTextLineBreakFragment on RichTextLineBreak {
    type
  }

  fragment RichTextTabFragment on RichTextTab {
    type
  }

  fragment RichTextParagraphFragmentNonRecursive on RichTextParagraph {
    children {
      ...RichTextLineBreakFragment
      ...RichTextTextFragment
      ...RichTextLinkFragmentNonRecursive
      ...RichTextBlockFragmentNonRecursive
      ...RichTextTabFragment
    }
    direction
    indent
    type
    version
    textFormat
    textStyle
  }

  fragment RichTextParagraphFragment on RichTextParagraph {
    ...RichTextParagraphFragmentNonRecursive
    children {
      ...RichTextLineBreakFragment
      ...RichTextTextFragment
      ...RichTextLinkFragment
      ...RichTextBlockFragment
      ...RichTextTabFragment
    }
  }

  fragment RichTextHeadingFragmentNonRecursive on RichTextHeading {
    children {
      ...RichTextTextFragment
      ...RichTextLinkFragmentNonRecursive
      ...RichTextBlockFragmentNonRecursive
      ...RichTextLineBreakFragment
      ...RichTextTabFragment
    }
    direction
    indent
    type
    version
    tag
  }

  fragment RichTextHeadingFragment on RichTextHeading {
    ...RichTextHeadingFragmentNonRecursive
    children {
      ...RichTextTextFragment
      ...RichTextLinkFragment
      ...RichTextBlockFragment
      ...RichTextLineBreakFragment
      ...RichTextTabFragment
    }
  }

  fragment RichTextHorizontalRuleFragment on RichTextHorizontalRule {
    type
  }

  fragment RichTextQuoteLevel4FragmentNonRecursive on RichTextQuote {
    direction
    indent
    type
    version
    children {
      ...RichTextTextFragment
      ...RichTextParagraphFragmentNonRecursive
      ...RichTextLineBreakFragment
      ...RichTextTabFragment
      ...RichTextBulletListLevel1FragmentNonRecursive
      ...RichTextNumberListLevel1FragmentNonRecursive
    }
  }

  fragment RichTextQuoteLevel3FragmentNonRecursive on RichTextQuote {
    ...RichTextQuoteLevel4FragmentNonRecursive
    children {
      ...RichTextTextFragment
      ...RichTextParagraphFragmentNonRecursive
      ...RichTextLineBreakFragment
      ...RichTextQuoteLevel4FragmentNonRecursive
      ...RichTextTabFragment
      ...RichTextBulletListLevel1FragmentNonRecursive
      ...RichTextNumberListLevel1FragmentNonRecursive
    }
  }

  fragment RichTextQuoteLevel2FragmentNonRecursive on RichTextQuote {
    ...RichTextQuoteLevel3FragmentNonRecursive
    children {
      ...RichTextTextFragment
      ...RichTextParagraphFragmentNonRecursive
      ...RichTextLineBreakFragment
      ...RichTextQuoteLevel3FragmentNonRecursive
      ...RichTextTabFragment
      ...RichTextBulletListLevel1FragmentNonRecursive
      ...RichTextNumberListLevel1FragmentNonRecursive
    }
  }

  fragment RichTextQuoteFragmentNonRecursive on RichTextQuote {
    ...RichTextQuoteLevel2FragmentNonRecursive
    children {
      ...RichTextTextFragment
      ...RichTextParagraphFragmentNonRecursive
      ...RichTextLineBreakFragment
      ...RichTextQuoteLevel2FragmentNonRecursive
      ...RichTextTabFragment
      ...RichTextBulletListLevel1FragmentNonRecursive
      ...RichTextNumberListLevel1FragmentNonRecursive
    }
  }

  fragment RichTextQuoteLevel4Fragment on RichTextQuote {
    direction
    indent
    type
    version
    children {
      ...RichTextTextFragment
      ...RichTextParagraphFragment
      ...RichTextLineBreakFragment
      ...RichTextTabFragment
      ...RichTextTableBulletListFragment
      ...RichTextTableNumberListFragment
    }
  }

  fragment RichTextQuoteLevel3Fragment on RichTextQuote {
    ...RichTextQuoteLevel4Fragment
    children {
      ...RichTextTextFragment
      ...RichTextParagraphFragment
      ...RichTextLineBreakFragment
      ...RichTextQuoteLevel4Fragment
      ...RichTextTabFragment
      ...RichTextTableBulletListFragment
      ...RichTextTableNumberListFragment
    }
  }

  fragment RichTextQuoteLevel2Fragment on RichTextQuote {
    ...RichTextQuoteLevel3Fragment
    children {
      ...RichTextTextFragment
      ...RichTextParagraphFragment
      ...RichTextLineBreakFragment
      ...RichTextQuoteLevel3Fragment
      ...RichTextTabFragment
      ...RichTextTableBulletListFragment
      ...RichTextTableNumberListFragment
    }
  }

  fragment RichTextQuoteFragment on RichTextQuote {
    ...RichTextQuoteLevel2Fragment
    children {
      ...RichTextTextFragment
      ...RichTextParagraphFragment
      ...RichTextLineBreakFragment
      ...RichTextQuoteLevel2Fragment
      ...RichTextTabFragment
      ...RichTextTableBulletListFragment
      ...RichTextTableNumberListFragment
    }
  }

  fragment RichTextCheckListItemLevel1FragmentNonRecursive on RichTextCheckListItem {
    children {
      ...RichTextTextFragment
      ...RichTextLinkFragmentNonRecursive
      ...RichTextCheckListLevel2FragmentNonRecursive
      ...RichTextBlockFragmentNonRecursive
      ...RichTextUploadFragment
      ...RichTextHorizontalRuleFragment
      ...RichTextLineBreakFragment
      ...RichTextTabFragment
    }
    direction
    indent
    type
    version
    checked
    value
  }

  fragment RichTextCheckListItemLevel1Fragment on RichTextCheckListItem {
    ...RichTextCheckListItemLevel1FragmentNonRecursive
    children {
      ...RichTextTextFragment
      ...RichTextLinkFragment
      ...RichTextCheckListLevel2Fragment
      ...RichTextBlockFragment
      ...RichTextUploadFragment
      ...RichTextLineBreakFragment
      ...RichTextTabFragment
    }
  }

  fragment RichTextCheckListItemLevel2FragmentNonRecursive on RichTextCheckListItem {
    children {
      ...RichTextTextFragment
      ...RichTextLinkFragmentNonRecursive
      ...RichTextCheckListLevel3FragmentNonRecursive
      ...RichTextBlockFragmentNonRecursive
      ...RichTextUploadFragment
      ...RichTextLineBreakFragment
      ...RichTextTabFragment
    }
    direction
    indent
    type
    version
    checked
    value
  }

  fragment RichTextCheckListItemLevel2Fragment on RichTextCheckListItem {
    ...RichTextCheckListItemLevel2FragmentNonRecursive
    children {
      ...RichTextTextFragment
      ...RichTextLinkFragment
      ...RichTextCheckListLevel3Fragment
      ...RichTextBlockFragment
      ...RichTextUploadFragment
      ...RichTextLineBreakFragment
      ...RichTextTabFragment
    }
  }

  fragment RichTextCheckListItemLevel3FragmentNonRecursive on RichTextCheckListItem {
    children {
      ...RichTextTextFragment
      ...RichTextLinkFragmentNonRecursive
      ...RichTextCheckListLevel4FragmentNonRecursive
      ...RichTextBlockFragmentNonRecursive
      ...RichTextUploadFragment
      ...RichTextLineBreakFragment
      ...RichTextTabFragment
    }
    direction
    indent
    type
    version
    checked
    value
  }

  fragment RichTextCheckListItemLevel3Fragment on RichTextCheckListItem {
    ...RichTextCheckListItemLevel3FragmentNonRecursive
    children {
      ...RichTextTextFragment
      ...RichTextLinkFragment
      ...RichTextCheckListLevel4Fragment
      ...RichTextBlockFragment
      ...RichTextUploadFragment
      ...RichTextLineBreakFragment
      ...RichTextTabFragment
    }
  }

  fragment RichTextCheckListItemLevel4FragmentNonRecursive on RichTextCheckListItem {
    children {
      ...RichTextTextFragment
      ...RichTextLinkFragmentNonRecursive
      ...RichTextBlockFragmentNonRecursive
      ...RichTextUploadFragment
      ...RichTextLineBreakFragment
      ...RichTextTabFragment
    }
    direction
    indent
    type
    version
    checked
    value
  }

  fragment RichTextCheckListItemLevel4Fragment on RichTextCheckListItem {
    ...RichTextCheckListItemLevel4FragmentNonRecursive
    children {
      ...RichTextTextFragment
      ...RichTextLinkFragment
      ...RichTextBlockFragment
      ...RichTextUploadFragment
      ...RichTextLineBreakFragment
      ...RichTextTabFragment
    }
  }

  fragment RichTextCheckListLevel1FragmentNonRecursive on RichTextCheckList {
    children {
      ...RichTextCheckListItemLevel1FragmentNonRecursive
    }
    direction
    indent
    type
    version
    start
    tag
  }

  fragment RichTextCheckListLevel1Fragment on RichTextCheckList {
    ...RichTextCheckListLevel1FragmentNonRecursive
    children {
      ...RichTextCheckListItemLevel1Fragment
    }
  }

  fragment RichTextCheckListLevel2FragmentNonRecursive on RichTextCheckList {
    children {
      ...RichTextCheckListItemLevel2FragmentNonRecursive
    }
    direction
    indent
    type
    version
    start
    tag
  }

  fragment RichTextCheckListLevel2Fragment on RichTextCheckList {
    ...RichTextCheckListLevel2FragmentNonRecursive
    children {
      ...RichTextCheckListItemLevel2Fragment
    }
  }

  fragment RichTextCheckListLevel3FragmentNonRecursive on RichTextCheckList {
    children {
      ...RichTextCheckListItemLevel3FragmentNonRecursive
    }
    direction
    indent
    type
    version
    start
    tag
  }

  fragment RichTextCheckListLevel3Fragment on RichTextCheckList {
    ...RichTextCheckListLevel3FragmentNonRecursive
    children {
      ...RichTextCheckListItemLevel3Fragment
    }
  }

  fragment RichTextCheckListLevel4FragmentNonRecursive on RichTextCheckList {
    children {
      ...RichTextCheckListItemLevel4FragmentNonRecursive
    }
    direction
    indent
    type
    version
    start
    tag
  }

  fragment RichTextCheckListLevel4Fragment on RichTextCheckList {
    ...RichTextCheckListLevel4FragmentNonRecursive
    children {
      ...RichTextCheckListItemLevel4Fragment
    }
  }

  fragment RichTextListItemFragmentLevel1NonRecursive on RichTextListItem {
    children {
      ...RichTextTextFragment
      ...RichTextLinkFragmentNonRecursive
      ...RichTextHeadingFragmentNonRecursive
      ...RichTextBulletListLevel2FragmentNonRecursive
      ...RichTextNumberListLevel2FragmentNonRecursive
      ...RichTextBlockFragmentNonRecursive
      ...RichTextUploadFragment
      ...RichTextHorizontalRuleFragment
      ...RichTextLineBreakFragment
      ...RichTextTabFragment
    }
    direction
    indent
    type
    version
    value
  }

  fragment RichTextListItemFragmentLevel1 on RichTextListItem {
    ...RichTextListItemFragmentLevel1NonRecursive
    children {
      ...RichTextTextFragment
      ...RichTextLinkFragment
      ...RichTextHeadingFragment
      ...RichTextBulletListLevel2Fragment
      ...RichTextNumberListLevel2Fragment
      ...RichTextBlockFragment
      ...RichTextUploadFragment
      ...RichTextHorizontalRuleFragment
      ...RichTextLineBreakFragment
      ...RichTextTabFragment
    }
  }

  fragment RichTextListItemFragmentLevel2NonRecursive on RichTextListItem {
    children {
      ...RichTextTextFragment
      ...RichTextLinkFragmentNonRecursive
      ...RichTextHeadingFragmentNonRecursive
      ...RichTextBulletListLevel3FragmentNonRecursive
      ...RichTextNumberListLevel3FragmentNonRecursive
      ...RichTextBlockFragmentNonRecursive
      ...RichTextUploadFragment
      ...RichTextHorizontalRuleFragment
      ...RichTextLineBreakFragment
      ...RichTextTabFragment
    }
    direction
    indent
    type
    version
    value
  }

  fragment RichTextListItemFragmentLevel2 on RichTextListItem {
    ...RichTextListItemFragmentLevel2NonRecursive
    children {
      ...RichTextTextFragment
      ...RichTextLinkFragment
      ...RichTextHeadingFragment
      ...RichTextBulletListLevel3Fragment
      ...RichTextNumberListLevel3Fragment
      ...RichTextBlockFragment
      ...RichTextUploadFragment
      ...RichTextHorizontalRuleFragment
      ...RichTextLineBreakFragment
      ...RichTextTabFragment
    }
  }

  fragment RichTextListItemFragmentLevel3NonRecursive on RichTextListItem {
    children {
      ...RichTextTextFragment
      ...RichTextLinkFragmentNonRecursive
      ...RichTextBulletListLevel4FragmentNonRecursive
      ...RichTextNumberListLevel4FragmentNonRecursive
      ...RichTextBlockFragmentNonRecursive
      ...RichTextUploadFragment
      ...RichTextHorizontalRuleFragment
      ...RichTextLineBreakFragment
      ...RichTextTabFragment
    }
    direction
    indent
    type
    version
    value
  }

  fragment RichTextListItemFragmentLevel3 on RichTextListItem {
    ...RichTextListItemFragmentLevel3NonRecursive
    children {
      ...RichTextTextFragment
      ...RichTextLinkFragment
      ...RichTextBulletListLevel4Fragment
      ...RichTextNumberListLevel4Fragment
      ...RichTextBlockFragment
      ...RichTextUploadFragment
      ...RichTextHorizontalRuleFragment
      ...RichTextLineBreakFragment
      ...RichTextTabFragment
    }
  }

  fragment RichTextListItemFragmentLevel4NonRecursive on RichTextListItem {
    children {
      ...RichTextTextFragment
      ...RichTextLinkFragmentNonRecursive
      ...RichTextBlockFragmentNonRecursive
      ...RichTextUploadFragment
      ...RichTextHorizontalRuleFragment
      ...RichTextLineBreakFragment
      ...RichTextTabFragment
    }
    direction
    indent
    type
    version
    value
  }

  fragment RichTextListItemFragmentLevel4 on RichTextListItem {
    ...RichTextListItemFragmentLevel4NonRecursive
    children {
      ...RichTextTextFragment
      ...RichTextLinkFragment
      ...RichTextBlockFragment
      ...RichTextUploadFragment
      ...RichTextLineBreakFragment
      ...RichTextTabFragment
    }
  }

  fragment RichTextBulletListLevel1FragmentNonRecursive on RichTextBulletList {
    children {
      ...RichTextListItemFragmentLevel1NonRecursive
    }
    direction
    indent
    type
    version
    start
    tag
  }

  fragment RichTextBulletListLevel1Fragment on RichTextBulletList {
    ...RichTextBulletListLevel1FragmentNonRecursive
    children {
      ...RichTextListItemFragmentLevel1
    }
  }

  fragment RichTextTableListItem on RichTextListItem {
    children {
      ...RichTextTextFragment
      ...RichTextLinkFragment
      ...RichTextHeadingFragment
      ...RichTextBlockFragment
      ...RichTextUploadFragment
      ...RichTextHorizontalRuleFragment
      ...RichTextLineBreakFragment
      ...RichTextTabFragment
    }
    direction
    indent
    type
    version
    value
  }

  fragment RichTextTableCheckListItem on RichTextCheckListItem {
    children {
      ...RichTextTextFragment
      ...RichTextLinkFragmentNonRecursive
      ...RichTextBlockFragmentNonRecursive
      ...RichTextUploadFragment
      ...RichTextHorizontalRuleFragment
      ...RichTextLineBreakFragment
      ...RichTextTabFragment
    }
    direction
    indent
    type
    version
    value
  }

  fragment RichTextTableBulletListFragment on RichTextBulletList {
    ...RichTextBulletListLevel1Fragment
    children {
      ...RichTextTableListItem
    }
  }

  fragment RichTextTableCheckListFragment on RichTextCheckList {
    ...RichTextCheckListLevel1Fragment
    children {
      ...RichTextTableCheckListItem
    }
  }

  fragment RichTextTableNumberListFragment on RichTextNumberList {
    ...RichTextNumberListLevel1Fragment
    children {
      ...RichTextTableListItem
    }
  }

  fragment RichTextBulletListLevel2FragmentNonRecursive on RichTextBulletList {
    children {
      ...RichTextListItemFragmentLevel2NonRecursive
    }
    direction
    indent
    type
    version
    start
    tag
  }

  fragment RichTextBulletListLevel2Fragment on RichTextBulletList {
    ...RichTextBulletListLevel2FragmentNonRecursive
    children {
      ...RichTextListItemFragmentLevel2
    }
  }

  fragment RichTextBulletListLevel3FragmentNonRecursive on RichTextBulletList {
    children {
      ...RichTextListItemFragmentLevel3NonRecursive
    }
    direction
    indent
    type
    version
    start
    tag
  }

  fragment RichTextBulletListLevel3Fragment on RichTextBulletList {
    ...RichTextBulletListLevel3FragmentNonRecursive
    children {
      ...RichTextListItemFragmentLevel3
    }
  }

  fragment RichTextBulletListLevel4FragmentNonRecursive on RichTextBulletList {
    children {
      ...RichTextListItemFragmentLevel4NonRecursive
    }
    direction
    indent
    type
    version
    start
    tag
  }

  fragment RichTextBulletListLevel4Fragment on RichTextBulletList {
    ...RichTextBulletListLevel4FragmentNonRecursive
    children {
      ...RichTextListItemFragmentLevel4
    }
  }

  fragment RichTextNumberListLevel1FragmentNonRecursive on RichTextNumberList {
    children {
      ...RichTextListItemFragmentLevel1NonRecursive
    }
    direction
    indent
    type
    version
    start
    tag
  }

  fragment RichTextNumberListLevel1Fragment on RichTextNumberList {
    ...RichTextNumberListLevel1FragmentNonRecursive
    children {
      ...RichTextListItemFragmentLevel1
    }
  }

  fragment RichTextNumberListLevel2FragmentNonRecursive on RichTextNumberList {
    children {
      ...RichTextListItemFragmentLevel2NonRecursive
    }
    direction
    indent
    type
    version
    start
    tag
  }

  fragment RichTextNumberListLevel2Fragment on RichTextNumberList {
    ...RichTextNumberListLevel2FragmentNonRecursive
    children {
      ...RichTextListItemFragmentLevel2
    }
  }

  fragment RichTextNumberListLevel3FragmentNonRecursive on RichTextNumberList {
    children {
      ...RichTextListItemFragmentLevel3NonRecursive
    }
    direction
    indent
    type
    version
    start
    tag
  }

  fragment RichTextNumberListLevel3Fragment on RichTextNumberList {
    ...RichTextNumberListLevel3FragmentNonRecursive
    children {
      ...RichTextListItemFragmentLevel3
    }
  }

  fragment RichTextNumberListLevel4FragmentNonRecursive on RichTextNumberList {
    children {
      ...RichTextListItemFragmentLevel4NonRecursive
    }
    direction
    indent
    type
    version
    start
    tag
  }

  fragment RichTextNumberListLevel4Fragment on RichTextNumberList {
    ...RichTextNumberListLevel4FragmentNonRecursive
    children {
      ...RichTextListItemFragmentLevel4
    }
  }

  fragment RichTextUploadFragment on RichTextUpload {
    #    format
    type
    version
    relationTo
    file: value {
      ...ImageRichTextFragment
    }
  }

  fragment RichTextBlockFragment on RichTextBlock {
    #    format
    type
    version
    fields {
      ...RichTextInternalLinkBlockFragment
      ...RichTextExternalLinkBlockFragment
      ...RichTextHubSpotLinkBlockFragment
      ...RichTextAssetLinkBlockFragment
      ...PayloadRichTextCalculatorLinkBlockFragment
      ...PayloadRichTextDialogLinkBlockFragment
      ...PayloadRichTextVisualiserLinkBlockFragment
    }
  }

  fragment RichTextBlockFragmentNonRecursive on RichTextBlock {
    #    format
    type
    version
    fields {
      ...RichTextInternalLinkBlockFragment
      ...RichTextExternalLinkBlockFragment
      ...RichTextHubSpotLinkBlockFragment
      ...RichTextAssetLinkBlockFragment
      ...PayloadRichTextCalculatorLinkBlockFragment
      ...PayloadRichTextVisualiserLinkBlockFragment
    }
  }

  fragment RichTextFragment on PayloadRichText {
    root {
      children {
        ...RichTextParagraphFragment
        ...RichTextHeadingFragment
        ...RichTextHorizontalRuleFragment
        ...RichTextQuoteFragment
        ...RichTextBulletListLevel1Fragment
        ...RichTextCheckListLevel1Fragment
        ...RichTextNumberListLevel1Fragment
        ...RichTextUploadFragment
        ...RichTextBlockFragment
        ...RichTextTableFragment
      }
      direction
      #      format
      indent
      type
      version
    }
  }

  fragment RichTextTableFragment on RichTextTable {
    type
    children {
      ...RichTextTableRowFragment
    }
    direction
    format
    indent
    version
    colWidths
  }

  fragment RichTextTableRowFragment on RichTextTableRow {
    type
    children {
      ...RichTextTableCellFragment
    }
    direction
    format
    indent
    version
  }

  fragment RichTextTableCellFragment on RichTextTableCell {
    type
    children {
      ...RichTextTextFragment
      ...RichTextLinkFragment
      ...RichTextParagraphFragment
      ...RichTextHeadingFragment
      ...RichTextQuoteFragment
      ...RichTextTableBulletListFragment
      ...RichTextTableCheckListFragment
      ...RichTextTableNumberListFragment
    }
    direction
    format
    indent
    version
  }
  fragment RichTextFragmentNonRecursive on PayloadRichText {
    root {
      children {
        ...RichTextParagraphFragmentNonRecursive
        ...RichTextHeadingFragmentNonRecursive
        ...RichTextHorizontalRuleFragment
        ...RichTextQuoteFragmentNonRecursive
        ...RichTextBulletListLevel1FragmentNonRecursive
        ...RichTextCheckListLevel1FragmentNonRecursive
        ...RichTextNumberListLevel1FragmentNonRecursive
        ...RichTextUploadFragment
        ...RichTextBlockFragmentNonRecursive
      }
      direction
      indent
      type
      version
    }
  }
`;
